import React from 'react';
import type { CharacterPortrait, Scenario, Scene, StageCharacter, StagePlacement } from '../../types';
import { defaultMoodFor } from '../../systems/character-runtime';
import { findLastDialogueStageDefaults, stageModeForScene } from '../../systems/stage-runtime';
import { listConfiguredEmotions } from './emotion-presets';
import inline from './inline-editor.module.css';

const PLACEMENTS: StagePlacement[] = ['gauche', 'centre', 'droite'];

interface InlineStagePanelProps {
  scene: Scene;
  scenario: Scenario;
  characters: CharacterPortrait[];
  editMode: boolean;
  /** Affiche + Perso et permet d'éditer placement / émotion. */
  allowAdd?: boolean;
  onChange: (patch: Partial<Scene>) => void;
}

function charLabel(characters: CharacterPortrait[], characterId: string): string {
  return characters.find((c) => c.id === characterId)?.name ?? characterId;
}

function emotionOptions(char: CharacterPortrait | undefined, current?: string): string[] {
  if (!char) return current ? [current] : [];
  const keys = listConfiguredEmotions(char.emotions);
  const list = keys.length > 0 ? [...keys] : ['neutre'];
  if (current && !list.includes(current)) list.push(current);
  return list;
}

export const InlineStagePanel: React.FC<InlineStagePanelProps> = ({
  scene,
  scenario,
  characters,
  editMode,
  allowAdd = true,
  onChange,
}) => {
  const mode = stageModeForScene(scene);

  const displayList =
    mode === 'custom' && scene.stageCharacters
      ? scene.stageCharacters
      : [];

  const patchStage = (next: StageCharacter[]) => {
    onChange({ stageCharacters: next, clearStage: undefined });
  };

  const clearStage = () => {
    onChange({ clearStage: true, stageCharacters: undefined });
  };

  const addCharacter = () => {
    const lastDialogue = findLastDialogueStageDefaults(scenario, scene.id);
    const preferredId = lastDialogue?.characterId;
    const unused =
      (preferredId && !displayList.some((s) => s.characterId === preferredId)
        ? characters.find((c) => c.id === preferredId)
        : undefined) ??
      characters.find((c) => !displayList.some((s) => s.characterId === c.id));
    if (!unused) return;
    const base =
      mode === 'custom' && scene.stageCharacters ? [...scene.stageCharacters] : [];
    const placement = lastDialogue?.placement ?? 'centre';
    const emotion = lastDialogue?.emotion ?? defaultMoodFor(unused);
    patchStage([
      ...base,
      {
        characterId: unused.id,
        placement,
        emotion,
      },
    ]);
  };

  const ensureCustomBase = (): StageCharacter[] =>
    mode === 'custom' && scene.stageCharacters ? [...scene.stageCharacters] : [];

  const updateEntry = (index: number, patch: Partial<StageCharacter>) => {
    const base = ensureCustomBase();
    base[index] = { ...base[index]!, ...patch };
    patchStage(base);
  };

  const removeEntry = (index: number) => {
    const base = ensureCustomBase();
    base.splice(index, 1);
    patchStage(base);
  };

  const availableCharactersForSlot = (index: number): CharacterPortrait[] => {
    const usedElsewhere = new Set(
      displayList.filter((_, i) => i !== index).map((s) => s.characterId)
    );
    return characters.filter(
      (c) => c.id === displayList[index]?.characterId || !usedElsewhere.has(c.id)
    );
  };

  const changeCharacter = (index: number, characterId: string) => {
    const portrait = characters.find((c) => c.id === characterId);
    if (!portrait) return;
    updateEntry(index, {
      characterId,
      emotion: defaultMoodFor(portrait),
    });
  };

  const canAdd = allowAdd && editMode && displayList.length < characters.length;
  const canEditChips = allowAdd && editMode;

  if (!editMode && displayList.length === 0) return null;

  const modeTag = mode === 'cleared' ? 'vide' : null;

  return (
    <div className={inline.stageRow}>
      <span className={inline.stageRowIcon} title="Personnages à l'écran" aria-hidden>
        🎭
      </span>

      <div className={inline.stageChips}>
        {displayList.length === 0 ? (
          <span className={inline.stageEmpty}>—</span>
        ) : (
          displayList.map((entry, index) => {
            const placement = (entry.placement ?? 'centre') as StagePlacement;
            const portrait = characters.find((c) => c.id === entry.characterId);
            const name = charLabel(characters, entry.characterId);
            const moods = emotionOptions(portrait, entry.emotion);
            const emotion = entry.emotion ?? moods[0] ?? 'neutre';

            if (canEditChips) {
              const pickerChars = availableCharactersForSlot(index);
              return (
                <span key={`${entry.characterId}-${index}`} className={inline.stageChipEdit}>
                  <select
                    className={inline.stageChipNameSelect}
                    value={entry.characterId}
                    onChange={(e) => changeCharacter(index, e.target.value)}
                    aria-label={`Personnage ${name}`}
                    title="Changer le personnage"
                  >
                    {pickerChars.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  <select
                    className={inline.stageChipSelect}
                    value={placement}
                    onChange={(e) =>
                      updateEntry(index, { placement: e.target.value as StagePlacement })
                    }
                    aria-label={`Placement ${name}`}
                  >
                    {PLACEMENTS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                  <select
                    className={inline.stageChipSelect}
                    value={emotion}
                    onChange={(e) => updateEntry(index, { emotion: e.target.value })}
                    aria-label={`Émotion ${name}`}
                  >
                    {moods.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={inline.stageChipRemove}
                    onClick={() => removeEntry(index)}
                    aria-label={`Retirer ${name}`}
                  >
                    ×
                  </button>
                </span>
              );
            }
            return (
              <span key={`${entry.characterId}-${index}`} className={inline.stageChip}>
                {name}
              </span>
            );
          })
        )}
      </div>

      {modeTag && <span className={inline.stageModeTag}>{modeTag}</span>}

      {editMode && allowAdd && (
        <div className={inline.stageActions}>
          {canAdd && (
            <button
              type="button"
              className={inline.stageLink}
              onClick={addCharacter}
              title="Ajouter un personnage"
            >
              + Perso
            </button>
          )}
          {displayList.length > 0 && (
            <button type="button" className={inline.stageLink} onClick={clearStage}>
              Vider
            </button>
          )}
        </div>
      )}
    </div>
  );
};
